const safeText = (value, maxLength = 160) =>
  `${value === null || value === undefined ? "" : value}`
    .trim()
    .slice(0, maxLength);

const finiteNumber = (value, fallback = 0) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
};

export const getCheckoutLineId = (item = {}, index = 0) =>
  safeText(
    item.lineId ||
      item.line_id ||
      item.cartLineId ||
      item.cart_line_id ||
      item.uuid ||
      item.key ||
      `${item.id || item.product || "line"}:${index}`,
    120,
  );

const normalizeSelectedOptions = (options = {}) => {
  if (Array.isArray(options)) {
    return options
      .map((option) => ({
        id: option.id || option.choiceId || option.choice_id,
        quantity: finiteNumber(option.quantity, 1),
        quantityExclusive:
          option.quantityExclusive === true || option.quantity_exclusive === true,
      }))
      .filter((option) => option.id !== null && option.id !== undefined);
  }

  return Object.values(options || {}).reduce((result, group) => {
    const choices = group?.selectedOptions || group?.selected_options || {};
    Object.entries(choices).forEach(([choiceId, choice]) => {
      result.push({
        id: choice?.id || choice?.choice || choiceId,
        quantity: finiteNumber(choice?.quantity, 1),
        quantityExclusive:
          group?.quantityExclusive === true ||
          group?.quantity_exclusive === true ||
          choice?.quantityExclusive === true ||
          choice?.quantity_exclusive === true,
      });
    });
    return result;
  }, []);
};

const normalizeExtraFields = (extraFields = {}) => {
  if (Array.isArray(extraFields)) {
    return extraFields
      .map((field) => ({
        id: field.id || field.fieldId || field.field_id,
        value: safeText(field.value, 500),
      }))
      .filter((field) => field.id !== null && field.id !== undefined);
  }

  return Object.entries(extraFields || {}).map(([fieldId, field]) => ({
    id: field?.id || fieldId,
    value: safeText(
      field && typeof field === "object" ? field.value : field,
      500,
    ),
  }));
};

const normalizeReference = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const resolved =
    value && typeof value === "object"
      ? value.id ?? value.value ?? value.pk
      : value;
  return safeText(resolved, 120) || null;
};

const normalizeComponents = (components = []) =>
  (Array.isArray(components) ? components : [])
    .map((component) => ({
      id: normalizeReference(
        component?.id ?? component?.product_id ?? component?.product,
      ),
      quantity: Math.max(0, finiteNumber(component?.quantity, 0)),
    }))
    .filter((component) => component.id)
    .slice(0, 100);

export const normalizeCheckoutLine = (item = {}, index = 0) => ({
  lineId: getCheckoutLineId(item, index),
  productId: item.product || item.productId || item.product_id || item.id,
  quantity: Math.max(0, finiteNumber(item.quantity, 0)),
  options: normalizeSelectedOptions(item.options || item.selectedOptions),
  extraFields: normalizeExtraFields(item.extraFields || item.extra_fields),
  name: safeText(item.name || item.title, 200),
  unitPrice: finiteNumber(item.total_price ?? item.price, 0),
  isPreordered:
    item.isPreordered === true || item.is_preordered === true,
  isPromotionLine:
    item.isPromotionLine === true || item.is_promotion_line === true,
  typeOfProduct: safeText(
    item.typeOfProduct ||
      item.type_of_product ||
      item.productType ||
      item.product_type ||
      item.product?.type_of_product ||
      item.product?.product_type,
    40,
  ).toLowerCase(),
  allowPreordering:
    item.allowPreordering === true ||
    item.allow_preordering === true ||
    item.product?.allowPreordering === true ||
    item.product?.allow_preordering === true,
  bookingSlot: normalizeReference(item.bookingSlot ?? item.booking_slot),
  resource: normalizeReference(item.resource ?? item.resource_id),
  resourceSlot: normalizeReference(item.resourceSlot ?? item.resource_slot),
  components: normalizeComponents(item.components),
  maxAddableQuantity:
    item.maxAddableQuantity ?? item.max_addable_quantity ?? null,
});

export const buildCheckoutLines = (cart = {}) => {
  if (Array.isArray(cart)) return cart.map(normalizeCheckoutLine);
  const regular = cart.cart || [];
  const promotion = cart.promo_items || [];
  return [
    ...regular.map(normalizeCheckoutLine),
    ...promotion.map((item, index) =>
      normalizeCheckoutLine(
        { ...item, isPromotionLine: true },
        regular.length + index,
      ),
    ),
  ];
};

const fnv1a = (value) => {
  let hash = 0x811c9dc5;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193);
  }
  return (hash >>> 0).toString(16).padStart(8, "0");
};

export const createCartFingerprint = (lines = []) =>
  `cart1-${fnv1a(
    JSON.stringify(
      lines.map((line) => [
        line.lineId,
        line.productId,
        line.quantity,
        line.options,
        line.extraFields,
        line.typeOfProduct,
        line.allowPreordering,
        line.bookingSlot,
        line.resource,
        line.resourceSlot,
        line.components,
      ]),
    ),
  )}`;

export const getActiveCheckoutLines = (lines = [], removedLineIds = []) => {
  const removed = new Set(removedLineIds.map((lineId) => `${lineId}`));
  return lines.filter((line) => !removed.has(`${line.lineId}`));
};

const getAdjustedQuantity = (line = {}) => {
  const value = line.quantity ?? line.adjustedQuantity ?? line.adjusted_quantity;
  const quantity = Number(value);
  return Number.isFinite(quantity) && quantity >= 0 ? quantity : null;
};

export const getEffectiveCheckoutLines = (lines = [], validation = {}) => {
  const removed = new Set(
    [...(validation.removedLineIds || []), ...(validation.removedLines || [])].map((line) =>
      `${typeof line === "object" ? line.lineId || line.line_id : line}`,
    ),
  );
  const adjusted = new Map(
    (validation.adjustedLines || []).map((line) => [
      `${line.lineId || line.line_id}`,
      getAdjustedQuantity(line),
    ]),
  );

  return lines.reduce((result, line) => {
    const lineId = `${line.lineId}`;
    if (removed.has(lineId)) return result;
    const quantity = adjusted.get(lineId);
    if ((quantity ?? line.quantity) <= 0) return result;
    result.push(quantity === null || quantity === undefined ? line : { ...line, quantity });
    return result;
  }, []);
};

export const getEffectiveLegacyCartItems = (
  items = [],
  validation = {},
  lineOffset = 0,
) => {
  const normalized = items.map((item, index) => ({
    item,
    lineId: getCheckoutLineId(item, lineOffset + index),
  }));
  const effectiveById = new Map(
    getEffectiveCheckoutLines(
      normalized.map(({ item, lineId }) => ({
        lineId,
        quantity: item.quantity,
      })),
      validation,
    ).map((line) => [`${line.lineId}`, line]),
  );

  return normalized.reduce((result, { item, lineId }) => {
    const effective = effectiveById.get(`${lineId}`);
    if (!effective) return result;
    result.push({
      ...item,
      // PromisePlace receives the rich legacy rows, while quote/validation use
      // compact normalized lines. Keep the original normalized id after
      // filtering so a surviving row is not assigned a new index-based id.
      line_id: lineId,
      ...(effective.quantity === item.quantity
        ? {}
        : { quantity: effective.quantity }),
    });
    return result;
  }, []);
};

export const classifyCheckoutLines = (lines = [], validation = {}) => {
  const removedById = new Map(
    (validation.removedLines || []).map((line) => [`${line.lineId}`, line]),
  );
  const adjustedById = new Map(
    (validation.adjustedLines || []).map((line) => [`${line.lineId}`, line]),
  );
  const retainedIds = new Set(
    (validation.retainedLineIds || []).map((lineId) => `${lineId}`),
  );
  const retainedById = new Map(
    (validation.retainedLines || []).map((line) => [`${line.lineId}`, line]),
  );
  const retained = [];
  const adjusted = [];
  const removed = [];

  lines.forEach((line) => {
    const lineId = `${line.lineId}`;
    if (removedById.has(lineId)) {
      removed.push({ ...line, ...removedById.get(lineId) });
    } else if (adjustedById.has(lineId)) {
      adjusted.push({ ...line, ...adjustedById.get(lineId) });
    } else if (!retainedIds.size || retainedIds.has(lineId)) {
      retained.push({ ...line, ...(retainedById.get(lineId) || {}) });
    }
  });

  (validation.removedLines || []).forEach((line) => {
    if (!lines.some((item) => `${item.lineId}` === `${line.lineId}`))
      removed.push(line);
  });
  return { retained, adjusted, removed };
};
